import { PayloadAction, createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import * as mealItemService from '../../../service/meal-item.service'
import { MealItem, MealItemRequest, MealItemSearchCriteria } from "../../../service/model/nutrition.model";
import { showNotification } from "../../alerts/alert.slice";
import { NotificationType } from "../../../shared/enums";
import { getAxiosErrorMessage } from "../../../service/axios/axios.call.service";

export interface MealItemState {
    isLoading: boolean;
}

const mealItemsAdapter = createEntityAdapter({
    selectId: (mealItem: MealItem) => mealItem.id,
    sortComparer: (a, b) => a.name?.localeCompare(b.name),
  })

export const initialState = mealItemsAdapter.getInitialState({
    isLoading: false,
} as MealItemState);

export const fetchMealItems = createAsyncThunk<MealItem[], MealItemSearchCriteria>(
    'mealItems/all',
    async (searchCriteria, {rejectWithValue}) => {
        return mealItemService.getMealItems(searchCriteria.searchTerm, searchCriteria.category).catch((errors) => {
            return rejectWithValue(errors);
        })
    }
);

export const createMealItem = createAsyncThunk<
    MealItem,
    MealItemRequest
>('mealItems/create', async (request, { rejectWithValue, dispatch }) => {
    return mealItemService
        .createMealItem(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Meal item created successfully',
                    type: NotificationType.Success,
                })
            );
            dispatch(
                fetchMealItems({
                    searchTerm: '',
                    category: '',
                })
            );
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

export const updateMealItem = createAsyncThunk<
    MealItem,
    MealItemRequest
>('mealItems/update', async (request, { rejectWithValue, dispatch }) => {
    return mealItemService
        .updateMealItem(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Meal item updated successfully',
                    type: NotificationType.Success,
                })
            );
            dispatch(
                fetchMealItems({
                    searchTerm: '',
                    category: '',
                })
            );
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

export const deleteMealItem = createAsyncThunk<
    number,
    number
>('mealItems/delete', async (id, { rejectWithValue, dispatch }) => {
    return mealItemService
        .deleteMealItem(id)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Meal item deleted successfully',
                    type: NotificationType.Success,
                })
            );
            dispatch(
                fetchMealItems({
                    searchTerm: '',
                    category: '',
                })
            );
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

const mealItemSlice = createSlice({
    name: 'meal-items',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
        .addCase(fetchMealItems.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(fetchMealItems.fulfilled, (state, action: PayloadAction<MealItem[]>) => {
            mealItemsAdapter.setAll(state, action.payload);
            state.isLoading = false;
        })
        .addCase(fetchMealItems.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(createMealItem.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(createMealItem.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(createMealItem.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(updateMealItem.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(updateMealItem.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(updateMealItem.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(deleteMealItem.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(deleteMealItem.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(deleteMealItem.rejected, (state) => {
            state.isLoading = false;
        });
    }
});

export const {
    selectAll: selectMealItems,
    selectById: selectMealItemById
} = mealItemsAdapter.getSelectors<RootState>((state) => state.mealItems);

export const selectIsLoading = (state: RootState) => state.mealItems.isLoading;

export default mealItemSlice.reducer;